import React, { createContext, useContext, useEffect, useState } from 'react';
import { login } from '../services/auth';
import { getSession } from '../services/service';

export const AuthContext = createContext(undefined);

const getUserData = () => {
  try {
    const stored = localStorage.getItem('userData');
    return stored ? JSON.parse(stored) : undefined;
  } catch {
    return undefined;
  }
};

const getCouncilInfo = () => {
  try {
    const stored = localStorage.getItem('councilInfo');
    return stored ? JSON.parse(stored) : undefined;
  } catch {
    return undefined;
  }
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(undefined);
  const [userData, setUserData] = useState(undefined);
  const [councilInfo, setCouncilInfo] = useState(undefined);

  useEffect(() => {
    if (!session) {
      setSession(getSession());
    }

    setUserData(getUserData());
    setCouncilInfo(getCouncilInfo());
  }, []);

  const handleLogin = async (username, password, isAdmin = false) => {
    try {
      const s = isAdmin ? await login(username, password) : await login(username, password);

      if (s) {
        setSession(s.session);
        localStorage.setItem('session', JSON.stringify(s.session));
        localStorage.setItem('userRole', s?.userData?.userType === 'ADMIN' ? 'admin' : 'user');

        // Persist userData so it survives refresh
        setUserData(s.userData);
        localStorage.setItem('userData', JSON.stringify(s.userData));

        // Persist councilInfo (councilInfo) so it survives refresh
        setCouncilInfo(s.councilInfo);
        localStorage.setItem('councilInfo', JSON.stringify(s.councilInfo));

        return s;
      } else {
        setSession(undefined);
        setUserData(undefined);
        setCouncilInfo(undefined);
        return undefined;
      }
    } catch (e) {
      setSession(undefined);
      setUserData(undefined);
      setCouncilInfo(undefined);
      throw e;
    }
  };

  const handleLogout = () => {
    setSession(undefined);
    setUserData(undefined);
    setCouncilInfo(undefined);
    localStorage.removeItem('session');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userData');
    localStorage.removeItem('councilInfo');
  };

  // Patches userData locally (and in localStorage) without a re-login or a
  // fresh fetch. userData/session here are managed by this context directly
  // (not through react-query), so there's no query cache to invalidate —
  // this is the equivalent of that for context-held state. Used e.g. after
  // a successful email verification to flip `isVerified` to true so the
  // verification modal stops re-opening.
  const updateUserData = (partialUserData) => {
    setUserData((prev) => {
      const next = { ...(prev || {}), ...partialUserData };
      localStorage.setItem('userData', JSON.stringify(next));
      return next;
    });
  };

  const value = {
    session,
    userData,
    councilInfo,
    login: handleLogin,
    logout: handleLogout,
    updateUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
